import {
  DatePicker,
  Divider,
  Dropdown,
  DropdownOption,
  Loader,
  Row,
  RowSize,
  ValidationWrapper,
} from "@/components";
import {
  DEFAULT_LOADING_TEXT,
  DEFAULT_SELECT_PLACEHOLDER_TEXT,
} from "@/constants";
import { appConfig } from "@/guards/auth/azure-b2c.config";
import { NumbersWOP } from "@/pages/requests/components/visit-details-form/components/wop/numbers";
import {
  NumberConfiguraions,
  WorkersOptions,
} from "@/pages/requests/components/visit-details-form/config";
import {
  useEdit,
  usePersonnelList,
  usePurposeList,
} from "@/pages/requests/components/visit-details-form/hooks";
import styles from "@/pages/requests/components/visit-details-form/styles.scss";
import {
  selectPersonnelsStore,
  selectRequestFormStore,
  selectUserData,
  useBoundStore,
} from "@/services";
import { getApplicationRoot, getBooleanConfigValue } from "@/utils";
import { getValidDate } from "@/utils/get-valid-date";
import clsx from "clsx";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { addYears } from "date-fns";
import { useFormContext } from "react-hook-form";
import {
  VisitDetailsField,
  VisitDetailsFieldLabel,
  VisitDetailsFormProps,
  VisitDetailsDateRangeStatus,
} from "./types";
import { ContractField } from "@/pages/requests/components/contract-details-form/types";

export const VisitDetailsForm: FC<VisitDetailsFormProps> = ({ data }) => {
  const { requestFormData, setRequestFormData } = useBoundStore(
    selectRequestFormStore,
  );

  const { user } = useBoundStore(selectUserData);

  const { selectedPersonnelMode, setSelectedPersonnelMode } = useBoundStore(
    selectPersonnelsStore,
  );

  const [isDirtyNumbers, setIsDirtyNumbers] = useState(true);

  const [isPresettedPersonnelMode] = useState(false);

  const { isLoading, purpose, error: purposeError } = usePurposeList();

  const { isLoadingPersonnels, personnels } = usePersonnelList({
    agreementNumber:
      requestFormData?.[ContractField.ContractType]?.ContractNo || "",
    supplierId: `${user?.Contractor?.AdnocId}` || "",
  });

  const hasPersonnels = useMemo(
    () => personnels && personnels?.length > 0,
    [personnels],
  );

  useEffect(() => {
    if (!data && !isPresettedPersonnelMode) {
      setSelectedPersonnelMode(
        !hasPersonnels && !selectedPersonnelMode
          ? WorkersOptions[0].Id
          : WorkersOptions[1].Id,
      );

      setIsDirtyNumbers(hasPersonnels);
    }
  }, [
    data,
    hasPersonnels,
    isPresettedPersonnelMode,
    selectedPersonnelMode,
    setSelectedPersonnelMode,
  ]);

  const {
    clearErrors,
    setValue,
    setError,
    formState: { errors, isValid },
    trigger,
  } = useFormContext();

  useEffect(() => {
    if (
      !requestFormData?.[VisitDetailsField.Purpose] &&
      !requestFormData?.[VisitDetailsField.From] &&
      !requestFormData?.[VisitDetailsField.To] &&
      !requestFormData?.[VisitDetailsField.NumbersGeneral] &&
      !requestFormData?.[VisitDetailsField.DateRange]
    ) {
      setError("init", { message: "VisitDetailsForm" });
    }
  }, [requestFormData, setError, trigger]);

  if (getBooleanConfigValue(appConfig?.FEATURES?.IS_DEBUG_ENABLED)) {
    /* eslint-disable */
    console.log("VisitDetailsForm", errors);
    console.log("VisitDetailsForm", isValid);
    /* eslint-enable */
  }

  const handleDataChange = useCallback(
    async (
      value: DropdownOption | Date | string | number | boolean,
      dropdownId: VisitDetailsField,
    ) => {
      clearErrors("init");
      clearErrors(dropdownId);
      setValue(dropdownId, value);
      setRequestFormData(dropdownId, value);

      return await trigger(dropdownId);
    },
    [clearErrors, setRequestFormData, setValue, trigger],
  );

  const isSomeFieldFilled = useMemo(() => {
    return (
      NumberConfiguraions.some((field: VisitDetailsField) => {
        return !!requestFormData?.[field];
      }) || hasPersonnels
    );
  }, [hasPersonnels, requestFormData]);

  const handleNumbersChange = useCallback(
    (value: string, field: VisitDetailsField) => {
      handleDataChange(Number(value), field);
      setIsDirtyNumbers(true);
    },
    [handleDataChange],
  );

  useEffect(() => {
    if (isDirtyNumbers) {
      handleDataChange(isSomeFieldFilled, VisitDetailsField.NumbersGeneral);
    }
  }, [handleDataChange, isDirtyNumbers, isSomeFieldFilled, trigger]);

  /**
   * Edit mode hook
   */
  useEdit({
    data,
    purpose,
    handleDataChange,
    handleNumbersChange,
    isPresettedPersonnelMode,
  });

  useEffect(() => {
    const fromDate = getValidDate(requestFormData?.[VisitDetailsField.From]);
    const toDate = getValidDate(requestFormData?.[VisitDetailsField.To]);

    if (!fromDate || !toDate) {
      return;
    }

    const maxTo = addYears(fromDate, 1);
    const isValidRange = toDate < maxTo;

    const prev = requestFormData?.[VisitDetailsField.DateRange];

    let newValue: VisitDetailsDateRangeStatus =
      VisitDetailsDateRangeStatus.Valid;

    if (toDate < fromDate) {
      newValue = VisitDetailsDateRangeStatus.EndBeforeStart;
    } else if (!isValidRange) {
      newValue = VisitDetailsDateRangeStatus.InvalidRange;
    }

    if (prev !== newValue) {
      handleDataChange(newValue, VisitDetailsField.DateRange);
    }
  }, [requestFormData, handleDataChange, clearErrors, setError]);

  return (
    <>
      <Row size={RowSize.Small}>
        <ValidationWrapper
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          errorMessage={
            purposeError ||
            (errors[VisitDetailsField.Purpose]?.message as string)
          }
        >
          <Dropdown
            containerClassName={styles.fullWidth}
            id={VisitDetailsField.Purpose}
            label={VisitDetailsFieldLabel[VisitDetailsField.Purpose]}
            placeholder={
              isLoading ? DEFAULT_LOADING_TEXT : DEFAULT_SELECT_PLACEHOLDER_TEXT
            }
            selectedOption={requestFormData?.[VisitDetailsField.Purpose]}
            options={purpose[VisitDetailsField.Purpose]}
            isNeedToHighlightSelectedItem
            onChange={handleDataChange}
            onFocus={() => {
              clearErrors(VisitDetailsField.Purpose);
            }}
            disabled={!purpose[VisitDetailsField.Purpose]?.length}
            mandatory
          />
        </ValidationWrapper>
      </Row>

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>

      <Row size={RowSize.Small} className={styles.dateGroup}>
        <ValidationWrapper
          errorMessage={errors[VisitDetailsField.From]?.message as string}
        >
          <DatePicker
            wrapperClassName={styles.fullWidth}
            labelClassName={styles.fullWidth}
            mode="single"
            inputLabel={VisitDetailsFieldLabel[VisitDetailsField.From]}
            inputPlaceholder="Select date"
            selected={getValidDate(requestFormData?.[VisitDetailsField.From])}
            onSelectedDateChange={date =>
              handleDataChange(date, VisitDetailsField.From)
            }
            floatingOptions={{
              strategy: "fixed",
            }}
            defaultMonth={
              requestFormData?.[VisitDetailsField.From]
                ? getValidDate(requestFormData?.[VisitDetailsField.From])
                : new Date()
            }
            portalWrapper={getApplicationRoot()}
            mandatory
          />
        </ValidationWrapper>

        <ValidationWrapper
          errorMessage={errors[VisitDetailsField.To]?.message as string}
        >
          <DatePicker
            wrapperClassName={styles.fullWidth}
            labelClassName={styles.fullWidth}
            mode="single"
            inputLabel={VisitDetailsFieldLabel[VisitDetailsField.To]}
            inputPlaceholder="Select date"
            selected={getValidDate(requestFormData?.[VisitDetailsField.To])}
            onSelectedDateChange={date =>
              handleDataChange(date, VisitDetailsField.To)
            }
            floatingOptions={{
              strategy: "fixed",
            }}
            defaultMonth={
              requestFormData?.[VisitDetailsField.To]
                ? getValidDate(requestFormData?.[VisitDetailsField.To])
                : new Date()
            }
            portalWrapper={getApplicationRoot()}
            mandatory
          />
        </ValidationWrapper>
      </Row>

      {errors[VisitDetailsField.DateRange] && (
        <Row
          size={RowSize.Small}
          className={clsx(styles.inputsGroup, styles.errorMessage)}
        >
          <span>{errors[VisitDetailsField.DateRange].message as string}</span>
        </Row>
      )}

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>

      {isLoadingPersonnels ? (
        <Loader />
      ) : (
        <>
          <div className={styles.numbers}>
            <NumbersWOP
              errors={errors}
              onNumbersChange={handleNumbersChange}
              hasPersonnels={hasPersonnels}
            />
          </div>

          {errors[VisitDetailsField.NumbersGeneral] && (
            <Row
              size={RowSize.Small}
              className={clsx(styles.inputsGroup, styles.errorMessage)}
            >
              <span>
                At least one of the optional fields must be filled in.
              </span>
            </Row>
          )}
        </>
      )}

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>
    </>
  );
};
